import { Role } from 'auth';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import '@/modules/common/styles/common.css';
import { formatNumberWithCommas } from '@/modules/common/utils/format-numbers.ts';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import IssuerRegisterClassDropDown from '@/modules/issuer-dashboard/components/IssuerRegisterClassDropDown.tsx';
import {
  IssuerAddClassModal,
  transferLockPeriodOptions,
} from '@/modules/issuer-dashboard/components/modals/IssuerAddClassModal.tsx';
import { useIssuerRegisterClassListQuery } from '@/modules/issuer-dashboard/queries/registers.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  BodyCell,
  Button,
  EmptyListState,
  ModalProviderContext,
  PageLayout,
  SearchField,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
} from 'ui';
import { useIsMobile } from 'ui/hooks';

type TypeHeadRegister =
  | 'Class'
  | 'Transfer lock period'
  | 'Price'
  | 'Min. Investment'
  | 'Current asset value'
  | 'Total holders'
  | 'Units held'
  | '';

const headItems: TableHeadColumn<TypeHeadRegister>[] = [
  { title: 'Class', className: 'min-w-[170px]' },
  { title: 'Transfer lock period', className: 'min-w-[180px]' },
  { title: 'Price', className: 'min-w-[80px]' },
  { title: 'Units held' },
  { title: 'Current asset value' },
  { title: 'Min. Investment' },
  { title: 'Total holders' },
  {
    title: '',
    className: 'text-left',
  },
];

const IssuerClasses = () => {
  const { isMobile } = useIsMobile();
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { openModal } = useContext(ModalProviderContext);
  const [search, setSearch] = useState<string>('');

  const {
    data: registerClassList,
    isLoading,
    isError,
  } = useIssuerRegisterClassListQuery({ search }, Number(registerId));

  const handleOpenAddClassModal = useCallback(() => {
    openModal(IssuerAddClassModal);
  }, [openModal]);

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    if (!registerClassList?.registers?.length) {
      return [];
    }

    return registerClassList?.registers?.map((data, i, all) => {
      const body: BodyCell[] = [
        {
          cell: (
            <span className="text-sm leading-[22px] text-[#0369A1] font-[500]">
              {data.class_name}
            </span>
          ),
        },
        {
          cell:
            transferLockPeriodOptions.find(({ value }) => value === data.transfer_lock_period)
              ?.label || '-',
        },
        {
          cell: `$${formatNumberWithCommas(Math.round(data.current_price_per_share * 100) / 100)}`,
        },
        {
          cell: formatNumberWithCommas(data.total_units),
        },
        {
          cell: `$${formatNumberWithCommas(Math.round(data.total_invested * 100) / 100)}`,
        },
        {
          cell: `$${formatNumberWithCommas(data.min_investment)}`,
        },

        {
          cell: formatNumberWithCommas(data.total_holders),
        },
        {
          cell: (
            <div className="flex items-center justify-end gap-1 select-none">
              <IssuerRegisterClassDropDown
                registerClass={data}
                placement={i > 0 ? 'left-end' : i === all.length - 1 ? 'left' : 'left-start'}
              />
            </div>
          ),
        },
      ];

      return {
        cells: body,
        onClick: () =>
          navigate(
            {
              pathname: issuerDashboardPaths.dynamic.classById(String(data.id)),
              search: params.toString(),
            },
            {
              state: { from: pathname + '?' + params.toString() },
            },
          ),
      };
    });
  }, [navigate, params, pathname, registerClassList?.registers]);

  const headerAction = (
    <div className="flex flex-wrap gap-2 flex-1 sm:justify-end justify-start">
      <SearchField placeholder="Search" onChange={setSearch} className="max-w-[230px]" />
      <Button className="w-[130px]" onClick={handleOpenAddClassModal}>
        Add new class
      </Button>
    </div>
  );

  return (
    <PageLayout
      title="Security classes"
      headerAction={headerAction}
      headerClassname="!border-none"
      contentClassName="!p-0"
    >
      {isLoading && (
        <div className="w-full h-full flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}
      {!isLoading && !!bodyItems.length && (
        <Table
          className="my-0"
          withRounded={isMobile}
          withBorderX={false}
          mobileFirstCellShowInHeadLeft={true}
          mobileLastCellShowInHeadRight={true}
          classNameHead="border-t-transparent"
          headItems={headItems}
          items={bodyItems}
          isLoading={isLoading}
          isError={isError}
        />
      )}
      {!isLoading && !bodyItems.length && (
        <EmptyListState
          title="No classes created yet"
          description="Click on Add new class button to create your first security class. Security classes are main entities into which investors can make investments"
          isLoading={isLoading}
          isSearchUsed={!!search}
          itemsCount={registerClassList?.registers?.length ?? 0}
        />
      )}
    </PageLayout>
  );
};

export default withPrivateAccess(IssuerClasses, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
